import React, { FunctionComponent } from "react";

import {
  ButtonsGroupChoiceField,
  FieldSpy,
  Heading,
  HideStack,
  Separator,
  SpinNumberInputField,
  Stack,
  SubmissionForm,
  SubmissionFormButtons,
  Text,
  ToggleField,
  TooltipHost,
  UseFormMethods
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { RestrictionType } from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useAddTenantDefaultUserAuth,
  useTenantDefaultUserAuth,
  useUpdateTenantDefaultUserAuth
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { mfaCountOptions, mfaMethodsOptions } from "../../_shared-blocks";
import { isEqualMfa } from "../../_shared-blocks/network-authention-settings-section/mfa-methods-options.utils";
import {
  TenantUsersAuthFormValues,
  UsersAuthFormProps
} from "./tenant-users-auth-form.types";

const tenantUsersAuthFormValidator = new Validator<TenantUsersAuthFormValues>(
  validator => ({
    sessionMaxDuration: validator.number().greaterThanOrEqual(1),
    pinLifetime: validator.number().greaterThanOrEqual(1),
    mfaFirstOptions: validator.custom().predicate({
      when: (_value, values) =>
        values.mfaMethodCount === 1 || values.mfaMethodCount === 2,
      then: validator.array().required()
    }),
    mfaSecondOptions: validator.custom().predicate({
      when: (_value, values) => values.mfaMethodCount === 2,
      then: validator.array().required()
    })
  })
);

const TenantUsersAuthFormBase: FunctionComponent<UsersAuthFormProps> = ({
  tenantInfo,
  tenantId
}) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserAuthenticationWrite
  );

  const {
    mutateAsync: addTenantDefaultUserAuth
  } = useAddTenantDefaultUserAuth();

  const {
    mutateAsync: updateTenantDefaultUserAuth
  } = useUpdateTenantDefaultUserAuth();

  const defaultValues: TenantUsersAuthFormValues = {
    sessionMaxDuration: tenantInfo?.sessionMaxDuration
      ? tenantInfo.sessionMaxDuration.toString()
      : undefined,
    quickPinRequired: !!tenantInfo?.quickPinRequired,
    mfaMethodCount: tenantInfo?.mfaMethodCount,
    mfaFirstOptions: tenantInfo?.mfaFirstOptions,
    mfaSecondOptions: tenantInfo?.mfaSecondOptions,
    restrictionType: tenantInfo?.restrictionType ?? RestrictionType.Worldwide,
    pinLifetime: tenantInfo?.pinLifetime
      ? tenantInfo.pinLifetime.toString()
      : undefined
  };

  const onSubmit = async (
    values: TenantUsersAuthFormValues,
    actions: Pick<
      UseFormMethods<TenantUsersAuthFormValues>,
      "reset" | "setValue"
    >
  ) => {
    const dto = {
      id: tenantId,
      sessionMaxDuration: values.sessionMaxDuration
        ? Number(values.sessionMaxDuration)
        : undefined,
      quickPinRequired: values.quickPinRequired,
      mfaMethodCount: values.mfaMethodCount,
      mfaFirstOptions: values.mfaFirstOptions,
      mfaSecondOptions: values.mfaSecondOptions,
      restrictionType: values.restrictionType,
      pinLifetime: values.pinLifetime ? Number(values.pinLifetime) : undefined
    };

    if (tenantInfo) {
      await updateTenantDefaultUserAuth(dto);
    } else {
      await addTenantDefaultUserAuth(dto);
    }
    actions.reset(values);
  };

  const mfaOptions = mfaMethodsOptions.map(o => ({
    ...o,
    onRenderText: option => option.text
  }));

  return (
    <SubmissionForm<TenantUsersAuthFormValues>
      initialValues={defaultValues}
      onSubmit={onSubmit}
      disabled={!hasWritePermission}
      styles={{ root: { maxWidth: 1170 } }}
      validate={tenantUsersAuthFormValidator.validate}
      hideButtons
      isEqual={isEqualMfa}
    >
      {(_state, actions) => {
        return (
          <Stack tokens={{ childrenGap: 24 }}>
            <Stack horizontalAlign="space-between" horizontal>
              <SectionTitle>Users Login Settings</SectionTitle>
              {hasWritePermission && (
                <SubmissionFormButtons
                  classNames={{}}
                  hideCancelButton
                  submitButtonProps={{ iconProps: { iconName: "Save" } }}
                />
              )}
            </Stack>
            <Stack
              horizontal
              tokens={{ childrenGap: 24 }}
              horizontalAlign="space-between"
              styles={{ root: { width: "100%" } }}
            >
              <Stack
                tokens={{ childrenGap: 16 }}
                styles={{ root: { width: "50%" } }}
              >
                <Heading variant="section-heading">Login Settings</Heading>
                <ToggleField
                  label="User quick PIN required"
                  name="quickPinRequired"
                />
                <TooltipHost content="The user can continue to extend their login session for this number of days using only a PIN to confirm their identity. After this time they will be prompted to log in again.">
                  <SpinNumberInputField
                    label="Maximum session extension duration with quick unlock PIN"
                    name="sessionMaxDuration"
                    min={1}
                  />
                </TooltipHost>
                <SpinNumberInputField
                  label="PIN expires and must be reset after (days)"
                  name="pinLifetime"
                  min={1}
                />
              </Stack>
              <Separator vertical />
              <Stack
                tokens={{ childrenGap: 16 }}
                styles={{ root: { width: "50%" } }}
              >
                <Heading variant="section-heading">
                  How Many MFA Methods for User?
                </Heading>
                <ButtonsGroupChoiceField<number>
                  name="mfaMethodCount"
                  options={mfaCountOptions}
                />
                <FieldSpy<number>
                  name="mfaMethodCount"
                  onChange={value => {
                    if (value === 0) {
                      actions.setValue("mfaFirstOptions", undefined);
                      actions.setValue("mfaSecondOptions", undefined);
                    }
                    if (value === 1) {
                      actions.setValue("mfaSecondOptions", undefined);
                    }
                  }}
                />
                <Separator />
                <FieldSpy<number> name="mfaMethodCount">
                  {value => {
                    if (typeof value === "undefined") {
                      return null;
                    }

                    return (
                      <>
                        <HideStack when={Number(value) === 0}>
                          <ButtonsGroupChoiceField<string>
                            label="MFA Step 1 Config"
                            name="mfaFirstOptions"
                            options={mfaOptions}
                            multiselect
                            required
                          />
                          <Text>
                            Users must set up at least one of the selected
                            options for 2 step verification at login.
                          </Text>
                        </HideStack>
                        <HideStack
                          when={Number(value) === 0 || Number(value) === 1}
                        >
                          <ButtonsGroupChoiceField<string>
                            label="MFA Step 2 Config"
                            required
                            name="mfaSecondOptions"
                            options={mfaOptions}
                            multiselect
                          />
                          <Text>
                            Users must set up a primary and a back-up
                            verification method from any of the selected
                            options.
                          </Text>
                          <Text>
                            <b>Note:</b> All MFA step 1 options should also be
                            in the MFA step 2 list
                          </Text>
                        </HideStack>
                      </>
                    );
                  }}
                </FieldSpy>
              </Stack>
            </Stack>
          </Stack>
        );
      }}
    </SubmissionForm>
  );
};

export const TenantUsersAuthForm: FunctionComponent<Pick<
  UsersAuthFormProps,
  "tenantId"
>> = ({ tenantId }) => {
  const tenantInfoQuery = useTenantDefaultUserAuth(tenantId);

  return (
    <QueryStateIndicator {...tenantInfoQuery} allowNullOrUndefined>
      {tenantInfo => (
        <TenantUsersAuthFormBase tenantInfo={tenantInfo} tenantId={tenantId} />
      )}
    </QueryStateIndicator>
  );
};
